import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  Badge,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Fab,
  Radio,
  RadioGroup,
  FormControlLabel,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  SwipeableDrawer,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as CartIcon,
  Restaurant as DineIcon,
  TakeoutDining as TakeawayIcon,
  DeliveryDining as ParcelIcon,
  LocalShipping as RiderIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  TableRestaurant as TableIcon,
  Print as PrintIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  SoupKitchen as KitchenIcon,
  CallSplit as SplitIcon,
  AddCircle as AddCircleIcon,
  Note as NoteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  ReceiptLong as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Money as MoneyIcon,
  CreditCard as CardIcon,
  PhoneAndroid as JazzCashIcon,
  LocalOffer as DealIcon,
} from '@mui/icons-material';

// ==================== CONSTANTS ====================
const ORDER_TYPES = [
  { value: 'dine_in', label: 'Dine In', labelUrdu: 'بیٹھ کر', icon: <DineIcon />, color: '#4caf50' },
  { value: 'takeaway', label: 'Takeaway', labelUrdu: 'پارسل', icon: <TakeawayIcon />, color: '#ff9800' },
  { value: 'delivery', label: 'Delivery', labelUrdu: 'ڈلیوری', icon: <ParcelIcon />, color: '#2196f3' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', labelUrdu: 'نقد', icon: <MoneyIcon /> },
  { value: 'card', label: 'Card', labelUrdu: 'کارڈ', icon: <CardIcon /> },
  { value: 'jazzcash', label: 'JazzCash', labelUrdu: 'جاز کیش', icon: <JazzCashIcon /> },
  { value: 'easypaisa', label: 'EasyPaisa', labelUrdu: 'ایزی پیسہ', icon: <JazzCashIcon /> },
];

// ==================== HELPERS ====================
const parseVariants = (variantsData) => {
  if (!variantsData) return [];
  if (Array.isArray(variantsData)) return variantsData;
  if (typeof variantsData === 'string') {
    try {
      const parsed = JSON.parse(variantsData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const formatPrice = (amount) => {
  const val = Number(amount) || 0;
  return `PKR ${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ==================== THERMAL RECEIPT ====================
const ThermalReceipt = ({ order, settings, paymentMethod, cashReceived, changeAmount }) => {
  const items = order?.items || [];
  const subtotal = Number(order?.total_amount) || 0;
  const tax = Number(order?.tax_amount) || 0;
  const service = Number(order?.service_charge) || 0;
  const discount = Number(order?.discount_amount) || 0;
  const total = Number(order?.final_amount) || subtotal;

  return (
    <Box sx={{
      width: '100%',
      maxWidth: 320,
      mx: 'auto',
      bgcolor: '#fff',
      color: '#000',
      fontFamily: '"Courier New", monospace',
      p: 2,
      fontSize: '0.75rem',
      lineHeight: 1.4,
      border: '1px solid #ddd',
      borderRadius: 2,
    }}>
      <Box sx={{ textAlign: 'center', mb: 1.5, borderBottom: '2px dashed #000', pb: 1 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', fontFamily: 'inherit' }}>
          {settings?.restaurant_name || 'RAATH DINE'}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', fontFamily: 'inherit', mt: 0.5 }}>
          {settings?.address || ''}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', fontFamily: 'inherit' }}>
          {settings?.phone || ''}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5, borderBottom: '1px dashed #000', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Order #:</span>
          <span style={{ fontWeight: 'bold' }}>{order?.id || order?.orderId || '---'}</span>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Date:</span>
          <span>{new Date().toLocaleString('en-GB')}</span>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Type:</span>
          <span style={{ textTransform: 'uppercase' }}>{order?.order_type || 'DINE IN'}</span>
        </Box>
        {order?.table_number && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Table:</span>
            <span>{order.table_number}</span>
          </Box>
        )}
        {order?.customer_name && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Customer:</span>
            <span>{order.customer_name}</span>
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 1.5, borderBottom: '1px dashed #000', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', mb: 0.5 }}>
          <span style={{ width: '45%' }}>Item</span>
          <span style={{ width: '15%', textAlign: 'center' }}>Qty</span>
          <span style={{ width: '40%', textAlign: 'right' }}>Amount</span>
        </Box>
        {items.length > 0 ? (
          items.map((item, idx) => (
            <Box key={idx} sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ width: '45%', wordBreak: 'break-word' }}>{item.item_name || item.name}</span>
                <span style={{ width: '15%', textAlign: 'center' }}>x{item.quantity}</span>
                <span style={{ width: '40%', textAlign: 'right' }}>
                  PKR {Number((item.unit_price + (item.modifiers_price || 0)) * item.quantity).toFixed(2)}
                </span>
              </Box>
              {item.modifiers && (
                <Typography sx={{ fontSize: '0.65rem', pl: 1, color: '#555', fontFamily: 'inherit' }}>
                  + {Array.isArray(item.modifiers) ? item.modifiers.join(', ') : item.modifiers}
                </Typography>
              )}
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: '0.7rem', textAlign: 'center', py: 1, fontFamily: 'inherit' }}>
            No items
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 1.5, borderBottom: '2px dashed #000', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Subtotal:</span>
          <span>PKR {subtotal.toFixed(2)}</span>
        </Box>
        {tax > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Tax:</span>
            <span>PKR {tax.toFixed(2)}</span>
          </Box>
        )}
        {service > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Service:</span>
            <span>PKR {service.toFixed(2)}</span>
          </Box>
        )}
        {discount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#d32f2f' }}>
            <span>Discount:</span>
            <span>-PKR {discount.toFixed(2)}</span>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '0.85rem', mt: 1 }}>
          <span>TOTAL:</span>
          <span>PKR {total.toFixed(2)}</span>
        </Box>
      </Box>

      <Box sx={{ mb: 1.5, borderBottom: '1px dashed #000', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Payment:</span>
          <span style={{ textTransform: 'uppercase' }}>{paymentMethod}</span>
        </Box>
        {paymentMethod === 'cash' && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Cash Received:</span>
              <span>PKR {Number(cashReceived || 0).toFixed(2)}</span>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
              <span>Change:</span>
              <span>PKR {Number(changeAmount || 0).toFixed(2)}</span>
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', fontFamily: 'inherit' }}>
          {settings?.bill_footer || 'Thank you for dining with us!'}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', fontFamily: 'inherit', mt: 0.5, color: '#666' }}>
          Powered by RAATH POS
        </Typography>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '1.2rem', letterSpacing: 2, fontFamily: 'inherit' }}>
            ||||||||||||||||||||||||||||
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', mt: 0.5 }}>CUT HERE</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ==================== MAIN COMPONENT ====================
const Billing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const passedTable = location.state;

  // Data states
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [settings, setSettings] = useState({});
  const [deals, setDeals] = useState([]);

  // UI flow states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [orderType, setOrderType] = useState('dine_in');
  const [selectedTable, setSelectedTable] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [modifierDialog, setModifierDialog] = useState(false);
  const [selectedItemForModifier, setSelectedItemForModifier] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [itemNote, setItemNote] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [riderName, setRiderName] = useState('');

  // Order extras
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [serviceCharge, setServiceCharge] = useState(0);
  const [orderNote, setOrderNote] = useState('');
  const [splitBillDialog, setSplitBillDialog] = useState(false);
  const [splitCount, setSplitCount] = useState(2);

  // Order & Payment
  const [createdOrder, setCreatedOrder] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [changeAmount, setChangeAmount] = useState(0);
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Deal dialog state
  const [dealDialog, setDealDialog] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);

  // ==================== HELPERS ====================
  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ==================== SAFE API ACCESS ====================
  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron === true) {
      console.log('[Billing] ✅ Electron API found!');
      return window.electronAPI;
    }
    console.log('[Billing] ⚠️ No Electron API, using browser storage');
    return null;
  }, []);

  // ==================== STORAGE ACCESS (Browser Mode) ====================
  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[Billing] Storage not available:', e);
      return null;
    }
  }, []);

  // ==================== LOAD DATA ====================
  const loadData = useCallback(async () => {
    try {
      const api = getAPI();
      let cats, items, tbls, sett, dealsData;

      if (api && api.isElectron === true) {
        console.log('[Billing] ✅ Electron mode - using electronAPI');
        [cats, items, tbls, sett, dealsData] = await Promise.all([
          api.getCategories(),
          api.getMenuItems(),
          api.getTables(),
          api.getSettings(),
          api.getDeals ? api.getDeals() : Promise.resolve([]),
        ]);
      } else {
        console.log('[Billing] Browser mode - using storage');
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');

        [cats, items, tbls, dealsData, sett] = await Promise.all([
          storage.getCategories(),
          storage.getMenuItems(),
          storage.getTables(),
          storage.getDeals ? storage.getDeals() : Promise.resolve([]),
          storage.getSettings(),
        ]);
      }

      setCategories(cats || []);
      setMenuItems(items || []);
      setTables((tbls || []).filter((t) => t.status === 'empty' || t.status === 'occupied'));
      setSettings(sett || {});
      setDeals(dealsData || []);

      if (passedTable?.tableId) {
        setSelectedTable(passedTable.tableId);
        setOrderType('dine_in');
      }
    } catch (err) {
      console.error('[Billing] Load error:', err);
      showMessage('❌ Data load nahi ho saki!', 'error');
    }
  }, [passedTable, getAPI, getStorage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ===== HELPERS =====
  const getItemsByCategory = (catId) => menuItems.filter((i) => i.category_id === catId && i.is_active === 1);
  const getCategoryById = (id) => categories.find((c) => c.id === id);

  const getFilteredItems = () => {
    let items = selectedCategory 
      ? getItemsByCategory(selectedCategory) 
      : menuItems.filter((i) => i.is_active === 1);
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((item) =>
        item.name?.toLowerCase().includes(q) ||
        item.name_urdu?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
      );
    }
    return items;
  };

  const getFilteredDeals = () => {
    if (!searchQuery.trim()) return deals;
    const q = searchQuery.toLowerCase();
    return deals.filter((deal) =>
      deal.name?.toLowerCase().includes(q) ||
      deal.name_urdu?.toLowerCase().includes(q) ||
      deal.description?.toLowerCase().includes(q)
    );
  };

  // ===== CART OPERATIONS =====
  const openModifierDialog = (item) => {
    setSelectedItemForModifier(item);
    setSelectedModifiers([]);
    setItemNote('');
    const variants = parseVariants(item.variants);
    if (variants.length > 0) {
      setSelectedVariant(variants[0]);
    } else {
      setSelectedVariant(null);
    }
    setModifierDialog(true);
  };

  const openDealDialog = (deal) => {
    setSelectedDeal(deal);
    setItemNote('');
    setDealDialog(true);
  };

  const addDealToCart = () => {
    if (!selectedDeal) return;

    const dealItems = parseVariants(selectedDeal.deal_items);
    const dealPrice = parseFloat(selectedDeal.price) || 0;

    const cartItem = {
      id: Date.now() + Math.random(),
      menu_item_id: null,
      name: selectedDeal.name,
      name_urdu: selectedDeal.name_urdu,
      unit_price: dealPrice,
      quantity: 1,
      modifiers: [],
      modifiers_price: 0,
      note: itemNote,
      kitchen_station: 'main',
      variant_size: null,
      variant_price: null,
      isDeal: true,
      dealItems: dealItems,
      dealId: selectedDeal.id,
    };

    setCart([...cart, cartItem]);
    setDealDialog(false);
    setSelectedDeal(null);
    setItemNote('');
    showMessage(`✅ ${selectedDeal.name} added to cart!`, 'success');
  };

  const toggleModifier = (modifier) => {
    if (selectedModifiers.find((m) => m.id === modifier.id)) {
      setSelectedModifiers(selectedModifiers.filter((m) => m.id !== modifier.id));
    } else {
      setSelectedModifiers([...selectedModifiers, modifier]);
    }
  };

  const addToCart = () => {
    if (!selectedItemForModifier) return;

    const variants = parseVariants(selectedItemForModifier.variants);
    const hasVariants = variants.length > 0;
    const basePrice = hasVariants && selectedVariant 
      ? parseFloat(selectedVariant.price) 
      : parseFloat(selectedItemForModifier.price) || 0;
    
    const modifiersTotal = selectedModifiers.reduce((sum, m) => sum + (parseFloat(m.price) || 0), 0);
    
    const cartItem = {
      id: Date.now() + Math.random(),
      menu_item_id: selectedItemForModifier.id,
      name: selectedItemForModifier.name,
      name_urdu: selectedItemForModifier.name_urdu,
      unit_price: basePrice,
      quantity: 1,
      modifiers: selectedModifiers,
      modifiers_price: modifiersTotal,
      note: itemNote,
      kitchen_station: selectedItemForModifier.kitchen_station,
      variant_size: hasVariants && selectedVariant ? selectedVariant.size : null,
      variant_price: hasVariants && selectedVariant ? selectedVariant.price : null,
      isDeal: false,
    };

    setCart([...cart, cartItem]);
    setModifierDialog(false);
    setSelectedItemForModifier(null);
    setSelectedVariant(null);
    showMessage(`✅ ${selectedItemForModifier.name} added to cart!`, 'success');
  };

  const updateQuantity = (cartId, delta) => {
    setCart(cart.map((item) => {
      if (item.id === cartId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter((item) => item.id !== cartId));
    showMessage('🗑️ Item removed from cart', 'info');
  };

  const cartTotal = cart.reduce((sum, item) => {
    const unitPrice = Number(item.unit_price) || 0;
    const modifiersPrice = Number(item.modifiers_price) || 0;
    const qty = Number(item.quantity) || 1;
    return sum + ((unitPrice + modifiersPrice) * qty);
  }, 0);

  const finalTotal = Math.max(0, cartTotal + (Number(serviceCharge) || 0) - (Number(discountAmount) || 0));

  // ===== ORDER CREATION =====
  const placeOrder = async () => {
    if (cart.length === 0) {
      showMessage('❌ Cart is empty!', 'error');
      return;
    }

    const processedItems = [];
    
    cart.forEach((item) => {
      if (item.isDeal) {
        processedItems.push({
          menu_item_id: null,
          item_name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          modifiers: [],
          modifiers_price: 0,
          notes: item.note || '',
          deal_id: item.dealId || null,
          is_deal: true,
          deal_name: item.name,
        });
      } else {
        processedItems.push({
          menu_item_id: item.menu_item_id,
          item_name: item.variant_size 
            ? `${item.name} (${item.variant_size})` 
            : item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          modifiers: item.modifiers.map((m) => m.name),
          modifiers_price: item.modifiers_price,
          notes: item.note || '',
          deal_id: null,
          is_deal: false,
          deal_name: null,
        });
      }
    });

    const orderPayload = {
      table_id: orderType === 'dine_in' ? selectedTable : null,
      order_type: orderType,
      customer_name: customerName || null,
      customer_phone: customerPhone || null,
      waiter_id: null,
      notes: `${orderNote}${deliveryAddress ? `\nAddress: ${deliveryAddress}` : ''}${riderName ? `\nRider: ${riderName}` : ''}`,
      items: processedItems,
      discount_amount: parseFloat(discountAmount) || 0,
      discount_percent: parseFloat(discountPercent) || 0,
      service_charge: parseFloat(serviceCharge) || 0,
    };

    try {
      const api = getAPI();
      
      // ✅ ELECTRON MODE
      if (api && api.isElectron === true) {
        console.log('[Billing] ✅ Creating order via Electron API');
        const result = await api.createOrder(orderPayload);
        
        const fullOrder = await api.getOrderById(result.orderId);
        fullOrder.table_number = tables.find(t => t.id === selectedTable)?.table_number;
        fullOrder.order_type = orderType;
        fullOrder.customer_name = customerName;
        
        setCreatedOrder(fullOrder);
        setCart([]);
        loadData();
        
        showMessage(`✅ Order #${result.orderId} | KOT #${result.kotNumber} Generated!`, 'success');
        openPaymentDialog();
        return;
      }

      // ✅ BROWSER MODE
      console.log('[Billing] ✅ Browser mode - using storage');
      const storage = await getStorage();
      if (!storage) throw new Error('Storage not available');
      
      const result = await storage.createOrder(orderPayload);
      console.log('[Billing] ✅ Order created via storage:', result);
      
      const fullOrder = {
  id: result.orderId,  // ✅ YEH LINE ADD KARO
  table_number: tables.find(t => t.id === selectedTable)?.table_number,
  order_type: orderType,
  customer_name: customerName,
  total_amount: result.totalAmount,
  final_amount: result.finalAmount,
  items: processedItems,
};
      
      setCreatedOrder(fullOrder);
      setCart([]);
      loadData();
      
      showMessage(`✅ Order #${result.orderId} | KOT #${result.kotNumber} Generated!`, 'success');
      openPaymentDialog();
      
    } catch (err) {
      console.error('Order error:', err);
      showMessage(`❌ Order failed: ${err.message}`, 'error');
    }
  };

  // ===== PAYMENT =====
  const openPaymentDialog = () => {
    setPaymentMethod('cash');
    setCashReceived('');
    setChangeAmount(0);
    setPaymentDialog(true);
  };

  const handleCashReceived = (val) => {
    setCashReceived(val);
    const received = parseFloat(val) || 0;
    const orderTotal = Number(createdOrder?.total_amount) || finalTotal || 0;
    setChangeAmount(Math.max(0, received - orderTotal));
  };

  const processPayment = async () => {
    if (!createdOrder) return;
    setIsProcessingPayment(true);
    
    try {
      const api = getAPI();
      
      // ✅ ELECTRON MODE
      if (api && api.isElectron === true) {
        await api.updateOrderStatus(createdOrder.id, 'billed');
        
        const updatedOrder = await api.getOrderById(createdOrder.id);
        updatedOrder.table_number = tables.find(t => t.id === selectedTable)?.table_number;
        updatedOrder.order_type = orderType;
        updatedOrder.customer_name = customerName;
        
        setCreatedOrder(updatedOrder);
        setPaymentDialog(false);
        setReceiptDialog(true);
        showMessage('✅ Payment successful!', 'success');
        setIsProcessingPayment(false);
        return;
      }

      // ✅ BROWSER MODE
      console.log('[Billing] ✅ Browser mode payment');
      const storage = await getStorage();
      if (!storage) throw new Error('Storage not available');
      
      await storage.updateOrderStatus(createdOrder.id, 'billed');
      
      const updatedOrder = await storage.getOrderById(createdOrder.id);
      updatedOrder.table_number = tables.find(t => t.id === selectedTable)?.table_number;
      updatedOrder.order_type = orderType;
      updatedOrder.customer_name = customerName;
      
      setCreatedOrder(updatedOrder);
      setPaymentDialog(false);
      setReceiptDialog(true);
      showMessage('✅ Payment successful!', 'success');
      setIsProcessingPayment(false);
      
    } catch (err) {
      console.error('Payment error:', err);
      showMessage(`❌ Payment failed: ${err.message}`, 'error');
      setIsProcessingPayment(false);
    }
  };

  const printReceipt = async () => {
    try {
      if (window.electronAPI?.printThermalReceipt) {
        await window.electronAPI.printThermalReceipt({
          orderId: createdOrder.id,
          paymentMethod,
          cashReceived,
          changeAmount,
        });
      } else {
        window.print();
      }
    } catch (err) {
      console.error('Print error:', err);
    }
  };

  const handleSplitBill = () => {
    setSplitBillDialog(true);
  };

  const confirmSplit = () => {
    setSplitBillDialog(false);
    showMessage(`✅ Bill split into ${splitCount} parts`, 'success');
  };

  // ===== STEPS =====
  const steps = ['Select Items', 'Order Details', 'Review'];

  const canProceed = () => {
    if (activeStep === 0) return cart.length > 0;
    if (activeStep === 1) {
      if (orderType === 'dine_in' && !selectedTable) return false;
      return true;
    }
    return true;
  };

  // ===== RENDER ITEM CARD =====
  const ItemCard = ({ item }) => {
    const variants = parseVariants(item.variants);
    const hasVariants = variants.length > 0;

    return (
      <Card
        onClick={() => openModifierDialog(item)}
        sx={{
          cursor: 'pointer',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.15s',
          '&:hover': { 
            borderColor: '#ff6b35', 
            boxShadow: '0 4px 12px rgba(255,107,53,0.15)',
          },
          height: '100%',
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Typography 
            component="span"
            sx={{ 
              fontWeight: 700, 
              color: '#1a1a2e', 
              fontSize: { xs: '0.85rem', sm: '0.95rem' }, 
              lineHeight: 1.2,
              display: 'block',
              mb: 0.3,
            }}
          >
            {item.name}
          </Typography>
          {item.name_urdu && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3, fontSize: '0.65rem' }}>
              {item.name_urdu}
            </Typography>
          )}
          
          {hasVariants ? (
            <Box sx={{ mt: 1 }}>
              {variants.slice(0, 2).map((v, idx) => (
                <Typography key={idx} variant="caption" sx={{ 
                  display: 'block', 
                  color: 'text.secondary',
                  fontSize: '0.7rem'
                }}>
                  {v.size}: <span style={{ color: '#2e7d32', fontWeight: 600 }}>{formatPrice(v.price)}</span>
                </Typography>
              ))}
              {variants.length > 2 && (
                <Typography variant="caption" sx={{ color: '#ff6b35', fontSize: '0.65rem' }}>
                  +{variants.length - 2} more
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.9rem', mt: 0.5 }}>
              {formatPrice(item.price)}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${item.prep_time}m`} variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />
            {item.is_veg === 1 && <Chip size="small" label="Veg" color="success" sx={{ height: 20, fontSize: '0.6rem' }} />}
            {hasVariants && <Chip size="small" label="Sizes" color="primary" sx={{ height: 20, fontSize: '0.6rem' }} />}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ===== RENDER DEAL CARD =====
  const DealCard = ({ deal }) => {
    const dealItems = parseVariants(deal.deal_items);

    return (
      <Card
        onClick={() => openDealDialog(deal)}
        sx={{
          cursor: 'pointer',
          borderRadius: 2,
          border: '2px solid #ff6b35',
          transition: 'all 0.15s',
          bgcolor: '#fff8f5',
          '&:hover': { 
            borderColor: '#e55a2b', 
            boxShadow: '0 4px 16px rgba(255,107,53,0.2)',
          },
          height: '100%',
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="body1" sx={{ 
              fontWeight: 700, 
              color: '#ff6b35', 
              fontSize: { xs: '0.9rem', sm: '1rem' }, 
              lineHeight: 1.2 
            }}>
              {deal.name}
            </Typography>
            <Chip 
              size="small" 
              label="DEAL" 
              sx={{ 
                height: 20, 
                fontSize: '0.6rem', 
                fontWeight: 700,
                bgcolor: '#ff6b35',
                color: '#fff'
              }} 
            />
          </Box>
          
          {deal.name_urdu && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3, fontSize: '0.65rem' }}>
              {deal.name_urdu}
            </Typography>
          )}

          <Box sx={{ mt: 1, p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px dashed #ffccbc' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a2e', display: 'block', mb: 0.3, fontSize: '0.65rem' }}>
              Includes:
            </Typography>
            {dealItems.length > 0 ? (
              dealItems.slice(0, 2).map((dItem, idx) => (
                <Typography key={idx} variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6rem' }}>
                  • {dItem.name || dItem.item_name || `Item ${idx + 1}`}
                </Typography>
              ))
            ) : (
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                No items listed
              </Typography>
            )}
            {dealItems.length > 2 && (
              <Typography variant="caption" sx={{ color: '#ff6b35', fontSize: '0.6rem' }}>
                +{dealItems.length - 2} more
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              {formatPrice(deal.price)}
            </Typography>
            <Button 
              size="small" 
              variant="contained" 
              sx={{ 
                bgcolor: '#ff6b35', 
                '&:hover': { bgcolor: '#e55a2b' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.7rem',
                py: 0.3,
                px: 1.5,
              }}
            >
              Add
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ===== CART SUMMARY PANEL =====
  const CartSummaryPanel = () => (
    <Paper sx={{ 
      height: '100%', 
      borderRadius: 3, 
      p: { xs: 1.5, sm: 2 }, 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: { xs: 'auto', md: 400 },
    }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
        Cart / کارٹ ({cart.reduce((sum, i) => sum + i.quantity, 0)} items)
      </Typography>
      <Box sx={{ flex: 1, overflow: 'auto', maxHeight: { xs: 200, md: 400 } }}>
        {cart.length > 0 ? (
          cart.map((item) => (
            <Box key={item.id} sx={{ 
              mb: 1, 
              p: { xs: 1, sm: 1.5 }, 
              bgcolor: '#fafafa', 
              borderRadius: 2, 
              border: '1px solid #eee',
              position: 'relative',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                    {item.name}
                    {item.isDeal && (
                      <Chip size="small" label="DEAL" color="secondary" sx={{ ml: 0.5, height: 16, fontSize: '0.55rem' }} />
                    )}
                    {item.variant_size && (
                      <Chip size="small" label={item.variant_size} color="primary" sx={{ ml: 0.5, height: 16, fontSize: '0.55rem' }} />
                    )}
                  </Typography>
                  {item.modifiers.length > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6rem' }}>
                      + {item.modifiers.map((m) => m.name).join(', ')}
                    </Typography>
                  )}
                  {item.isDeal && item.dealItems && item.dealItems.length > 0 && (
                    <Typography variant="caption" sx={{ color: '#ff6b35', display: 'block', fontSize: '0.6rem' }}>
                      Includes: {item.dealItems.slice(0, 2).map(d => d.name || d.item_name || 'Item').join(', ')}
                      {item.dealItems.length > 2 && ` +${item.dealItems.length - 2} more`}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)} sx={{ p: 0.5 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => updateQuantity(item.id, -1)} sx={{ bgcolor: '#f0f0f0', width: 22, height: 22 }}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 20, textAlign: 'center', fontSize: '0.8rem' }}>
                    {item.quantity}
                  </Typography>
                  <IconButton size="small" onClick={() => updateQuantity(item.id, 1)} sx={{ bgcolor: '#f0f0f0', width: 22, height: 22 }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.8rem' }}>
                  {formatPrice((item.unit_price + item.modifiers_price) * item.quantity)}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4, fontSize: '0.85rem' }}>
            Cart is empty
          </Typography>
        )}
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.75rem', sm: '0.85rem' }}>Subtotal</Typography>
        <Typography variant="body2" fontWeight={600} fontSize={{ xs: '0.75rem', sm: '0.85rem' }}>{formatPrice(cartTotal)}</Typography>
      </Box>
      {serviceCharge > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.75rem', sm: '0.85rem' }}>Service</Typography>
          <Typography variant="body2" fontWeight={600} fontSize={{ xs: '0.75rem', sm: '0.85rem' }}>{formatPrice(serviceCharge)}</Typography>
        </Box>
      )}
      {discountAmount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="error" fontSize={{ xs: '0.75rem', sm: '0.85rem' }}>Discount</Typography>
          <Typography variant="body2" color="error" fontWeight={600} fontSize={{ xs: '0.75rem', sm: '0.85rem' }}>-{formatPrice(discountAmount)}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid #1a1a2e' }}>
        <Typography variant="subtitle1" fontWeight={700} fontSize={{ xs: '0.9rem', sm: '1rem' }}>Total</Typography>
        <Typography variant="subtitle1" fontWeight={700} color="#2e7d32" fontSize={{ xs: '0.9rem', sm: '1rem' }}>{formatPrice(finalTotal)}</Typography>
      </Box>
    </Paper>
  );

  // ==================== MAIN RETURN ====================
  return (
    <Box sx={{ 
      height: 'calc(100vh - 120px)', 
      display: 'flex', 
      flexDirection: 'column',
      px: { xs: 1, sm: 2 },
      pb: { xs: 8, sm: 0 },
      overflow: 'hidden',
    }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ===== TOP BAR ===== */}
      <Paper sx={{ 
        p: { xs: 1, sm: 1.5 }, 
        mb: 1.5, 
        borderRadius: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: { xs: 1, sm: 1.5 },
      }}>
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            flex: 1, 
            minWidth: { xs: '100%', sm: 250 },
            '& .MuiStepLabel-label': { fontSize: { xs: '0.65rem', sm: '0.8rem' } },
            '& .MuiStepIcon-root': { fontSize: { xs: '1.2rem', sm: '1.5rem' } },
          }}
        >
          {steps.map((label, idx) => (
            <Step key={label} completed={activeStep > idx}>
              <StepLabel>{isMobile ? label.split(' ')[0] : label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ 
          display: 'flex', 
          gap: 0.5, 
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}>
          {ORDER_TYPES.map((type) => (
            <Button
              key={type.value}
              variant={orderType === type.value ? 'contained' : 'outlined'}
              onClick={() => setOrderType(type.value)}
              size="small"
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                bgcolor: orderType === type.value ? type.color : 'transparent',
                borderColor: type.color,
                color: orderType === type.value ? '#fff' : type.color,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
                py: { xs: 0.3, sm: 0.5 },
                px: { xs: 1, sm: 1.5 },
                flex: { xs: 1, sm: 'none' },
              }}
            >
              {isMobile ? type.icon : type.label}
              {!isMobile && (
                <Typography component="span" variant="caption" sx={{ ml: 0.3, opacity: 0.8, fontSize: '0.6rem' }}>
                  ({type.labelUrdu})
                </Typography>
              )}
            </Button>
          ))}
        </Box>

        {orderType === 'dine_in' && (
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
            <InputLabel sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Table</InputLabel>
            <Select
              value={selectedTable || ''}
              label="Table"
              onChange={(e) => setSelectedTable(e.target.value)}
              startAdornment={<TableIcon fontSize="small" sx={{ mr: 0.5, color: '#4caf50' }} />}
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              {tables.map((t) => (
                <MenuItem key={t.id} value={t.id} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  {t.table_number} {t.status === 'occupied' && '• Occupied'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Paper>

      {/* ===== MAIN CONTENT ===== */}
      <Grid container spacing={1.5} sx={{ flex: 1, overflow: 'hidden', alignItems: 'flex-start' }}>
        
        {/* ===== STEP 0: SELECT ITEMS ===== */}
        {activeStep === 0 && (
          <>
            <Grid item xs={12}>
              <Paper sx={{ 
                borderRadius: 3, 
                p: { xs: 1, sm: 1.5 }, 
                overflow: 'hidden',
                height: '100%',
              }}>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items / آئٹم تلاش کریں..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1.5 }}
                />

                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 700, 
                  mb: 1, 
                  color: '#1a1a2e',
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                }}>
                  Categories / کیٹیگریز
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 0.8, 
                  overflowX: 'auto',
                  pb: 1.5,
                  '&::-webkit-scrollbar': { height: 3 },
                  '&::-webkit-scrollbar-track': { bgcolor: '#f0f0f0', borderRadius: 2 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 2 }
                }}>
                  <Button
                    key="all-categories"
                    size="small"
                    variant={selectedCategory === null ? 'contained' : 'outlined'}
                    onClick={() => setSelectedCategory(null)}
                    sx={{ 
                      borderRadius: 4, 
                      bgcolor: selectedCategory === null ? '#ff6b35' : 'transparent',
                      borderColor: '#ff6b35',
                      color: selectedCategory === null ? '#fff' : '#ff6b35',
                      whiteSpace: 'nowrap',
                      px: { xs: 1.5, sm: 2.5 },
                      py: { xs: 0.4, sm: 0.6 },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                      flexShrink: 0,
                    }}
                  >
                    All ({getFilteredItems().length})
                  </Button>
                  
                  {categories.map((cat) => {
                    const count = getItemsByCategory(cat.id).length;
                    return (
                      <Button
                        key={cat.id}
                        size="small"
                        variant={selectedCategory === cat.id ? 'contained' : 'outlined'}
                        onClick={() => setSelectedCategory(cat.id)}
                        sx={{ 
                          borderRadius: 4, 
                          bgcolor: selectedCategory === cat.id ? cat.color : 'transparent',
                          borderColor: cat.color,
                          color: selectedCategory === cat.id ? '#fff' : cat.color,
                          whiteSpace: 'nowrap',
                          px: { xs: 1.5, sm: 2.5 },
                          py: { xs: 0.4, sm: 0.6 },
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: { xs: '0.65rem', sm: '0.8rem' },
                          flexShrink: 0,
                        }}
                      >
                        {isMobile ? (cat.name.length > 10 ? cat.name.substring(0, 8) + '..' : cat.name) : cat.name}
                        <Typography component="span" sx={{ ml: 0.5, opacity: 0.6, fontSize: '0.6rem' }}>
                          ({count})
                        </Typography>
                      </Button>
                    );
                  })}
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newVal) => setActiveTab(newVal)}
                    variant="fullWidth"
                    sx={{ 
                      '& .MuiTabs-flexContainer': { gap: 0.5 },
                      '& .MuiTab-root': { 
                        textTransform: 'none', 
                        fontWeight: 600,
                        borderRadius: '8px 8px 0 0',
                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                        minHeight: { xs: 36, sm: 40 },
                        px: { xs: 0.5, sm: 1 },
                      }
                    }}
                  >
                    <Tab 
                      key="menu-tab"
                      icon={<RestaurantIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                      iconPosition="start"
                      label={isMobile ? '' : 'Menu'}
                      sx={{ '& .MuiTab-iconWrapper': { mr: { xs: 0, sm: 0.5 } } }}
                    />
                    <Tab 
                      key="deals-tab"
                      icon={<DealIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                      iconPosition="start"
                      label={isMobile ? '' : 'Deals'}
                      sx={{ '& .MuiTab-iconWrapper': { mr: { xs: 0, sm: 0.5 } } }}
                    />
                  </Tabs>
                </Box>

                {activeTab === 0 && (
                  <Box sx={{ maxHeight: { xs: 'calc(100vh - 500px)', md: 'calc(100vh - 480px)' }, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 700, 
                      color: '#1a1a2e', 
                      mb: 1,
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    }}>
                      {selectedCategory ? getCategoryById(selectedCategory)?.name : 'All Items'}
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                        ({getFilteredItems().length})
                      </Typography>
                    </Typography>

                    {getFilteredItems().length > 0 ? (
                      <Grid container spacing={1}>
                        {getFilteredItems().map((item) => (
                          <Grid item xs={6} sm={4} md={3} key={item.id}>
                            <ItemCard item={item} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        <SearchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>No items found</Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          {selectedCategory ? 'اس کیٹیگری میں کوئی آئٹم نہیں' : 'کوئی آئٹم نہیں ملا'}
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                          sx={{ mt: 2, borderRadius: 2 }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box sx={{ maxHeight: { xs: 'calc(100vh - 500px)', md: 'calc(100vh - 480px)' }, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 700, 
                      color: '#ff6b35', 
                      mb: 1,
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    }}>
                      Special Deals / سپیشل ڈیلز
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                        ({getFilteredDeals().length})
                      </Typography>
                    </Typography>

                    {getFilteredDeals().length > 0 ? (
                      <Grid container spacing={1}>
                        {getFilteredDeals().map((deal) => (
                          <Grid item xs={12} sm={6} key={deal.id}>
                            <DealCard deal={deal} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        <DealIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3, color: '#ff6b35' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>No deals found</Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          کوئی ڈیل نہیں ملی
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => { setSearchQuery(''); }}
                          sx={{ mt: 2, borderRadius: 2 }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>

            {!isMobile && (
              <Grid item md={4}>
                <CartSummaryPanel />
              </Grid>
            )}
          </>
        )}

        {/* ===== STEP 1: ORDER DETAILS ===== */}
        {activeStep === 1 && (
          <>
            <Grid item xs={12} md={8}>
              <Paper sx={{ borderRadius: 3, p: { xs: 1.5, sm: 2.5 }, overflow: 'auto' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  Order Details / آرڈر کی تفصیلات
                </Typography>

                <Grid container spacing={1.5}>
                  {orderType === 'delivery' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          label="Customer Name" 
                          value={customerName} 
                          onChange={(e) => setCustomerName(e.target.value)} 
                          InputProps={{ 
                            startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment> 
                          }} 
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          label="Phone" 
                          value={customerPhone} 
                          onChange={(e) => setCustomerPhone(e.target.value)} 
                          InputProps={{ 
                            startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment> 
                          }} 
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          multiline 
                          rows={2} 
                          label="Address" 
                          value={deliveryAddress} 
                          onChange={(e) => setDeliveryAddress(e.target.value)} 
                          InputProps={{ 
                            startAdornment: <InputAdornment position="start"><LocationIcon fontSize="small" color="action" /></InputAdornment> 
                          }} 
                        />
                      </Grid>
                    </>
                  )}

                  {orderType === 'takeaway' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          label="Customer Name (Optional)" 
                          value={customerName} 
                          onChange={(e) => setCustomerName(e.target.value)} 
                          InputProps={{ 
                            startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment> 
                          }} 
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          label="Phone (Optional)" 
                          value={customerPhone} 
                          onChange={(e) => setCustomerPhone(e.target.value)} 
                          InputProps={{ 
                            startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment> 
                          }} 
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          label="Pickup Person" 
                          value={riderName} 
                          onChange={(e) => setRiderName(e.target.value)} 
                          InputProps={{ 
                            startAdornment: <InputAdornment position="start"><RiderIcon fontSize="small" color="action" /></InputAdornment> 
                          }} 
                        />
                      </Grid>
                    </>
                  )}

                  {orderType === 'dine_in' && selectedTable && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8rem' }}>
                          <TableIcon fontSize="small" /> Table: {tables.find((t) => t.id === selectedTable)?.table_number || 'Unknown'}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1a1a2e', fontSize: '0.85rem' }}>
                      Discount & Charges
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6} md={3}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          type="number" 
                          label="Discount %" 
                          value={discountPercent} 
                          onChange={(e) => { 
                            setDiscountPercent(e.target.value); 
                            setDiscountAmount((cartTotal * (e.target.value / 100)).toFixed(2)); 
                          }} 
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} 
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          type="number" 
                          label="Discount Amount" 
                          value={discountAmount} 
                          onChange={(e) => { 
                            setDiscountAmount(e.target.value); 
                            setDiscountPercent(0); 
                          }} 
                          InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} 
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          type="number" 
                          label="Service Charge" 
                          value={serviceCharge} 
                          onChange={(e) => setServiceCharge(parseFloat(e.target.value) || 0)} 
                          InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} 
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          multiline 
                          rows={2} 
                          label="Order Note" 
                          value={orderNote} 
                          onChange={(e) => setOrderNote(e.target.value)} 
                          InputProps={{ 
                            startAdornment: <InputAdornment position="start"><NoteIcon fontSize="small" color="action" /></InputAdornment> 
                          }} 
                          placeholder="Special instructions..." 
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <CartSummaryPanel />
            </Grid>
          </>
        )}

        {/* ===== STEP 2: REVIEW ===== */}
        {activeStep === 2 && (
          <>
            <Grid item xs={12} md={8}>
              <Paper sx={{ borderRadius: 3, p: { xs: 1.5, sm: 2.5 }, overflow: 'auto' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', textAlign: 'center', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  Order Summary / خلاصہ
                </Typography>

                <Box sx={{ maxWidth: 500, mx: 'auto' }}>
                  <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: '2px dashed #ddd', bgcolor: '#fafafa' }}>
                    <Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 700, color: '#1a1a2e', mb: 1.5, fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                      {settings.restaurant_name || 'RAATH DINE'}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />

                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                        Order Type: <strong>{ORDER_TYPES.find((t) => t.value === orderType)?.label}</strong>
                      </Typography>
                      {orderType === 'dine_in' && <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Table: <strong>{tables.find((t) => t.id === selectedTable)?.table_number}</strong></Typography>}
                      {orderType === 'delivery' && customerName && <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Customer: <strong>{customerName}</strong></Typography>}
                      {orderType === 'takeaway' && customerName && <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Customer: <strong>{customerName}</strong></Typography>}
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    {cart.map((item, idx) => (
                      <Box key={item.id} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {idx + 1}. {item.name} {item.variant_size && <span style={{color:'#ff6b35'}}>({item.variant_size})</span>} {item.isDeal && <Chip size="small" label="DEAL" color="secondary" sx={{ height: 16, fontSize: '0.5rem', ml: 0.3 }} />} x{item.quantity}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {formatPrice((item.unit_price + item.modifiers_price) * item.quantity)}
                          </Typography>
                        </Box>
                        {item.modifiers.length > 0 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', pl: 1, fontSize: '0.6rem' }}>
                            + {item.modifiers.map((m) => m.name).join(', ')}
                          </Typography>
                        )}
                        {item.isDeal && item.dealItems && item.dealItems.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#ff6b35', pl: 1, display: 'block', fontSize: '0.6rem' }}>
                            Includes: {item.dealItems.map(d => d.name || d.item_name || 'Item').join(', ')}
                          </Typography>
                        )}
                        {item.note && <Typography variant="caption" sx={{ color: '#ff9800', pl: 1, display: 'block', fontSize: '0.6rem' }}>Note: {item.note}</Typography>}
                      </Box>
                    ))}

                    <Divider sx={{ my: 1.5 }} />

                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.7rem', sm: '0.8rem' }}>Subtotal</Typography>
                        <Typography variant="body2" fontWeight={600} fontSize={{ xs: '0.7rem', sm: '0.8rem' }}>{formatPrice(cartTotal)}</Typography>
                      </Box>
                      {serviceCharge > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.7rem', sm: '0.8rem' }}>Service Charge</Typography>
                          <Typography variant="body2" fontWeight={600} fontSize={{ xs: '0.7rem', sm: '0.8rem' }}>{formatPrice(serviceCharge)}</Typography>
                        </Box>
                      )}
                      {discountAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="body2" color="error" fontSize={{ xs: '0.7rem', sm: '0.8rem' }}>Discount</Typography>
                          <Typography variant="body2" color="error" fontWeight={600} fontSize={{ xs: '0.7rem', sm: '0.8rem' }}>-{formatPrice(discountAmount)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid #1a1a2e', mt: 0.5 }}>
                        <Typography variant="h6" fontWeight={700} fontSize={{ xs: '0.9rem', sm: '1rem' }}>Total</Typography>
                        <Typography variant="h6" fontWeight={700} color="#2e7d32" fontSize={{ xs: '0.9rem', sm: '1rem' }}>{formatPrice(finalTotal)}</Typography>
                      </Box>
                    </Box>

                    {orderNote && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: '#fff3e0', borderRadius: 1, borderLeft: '3px solid #ff9800' }}>
                        <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, fontSize: '0.7rem' }}>
                          Note: {orderNote}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      startIcon={<BackIcon />}
                      onClick={() => setActiveStep(1)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, py: { xs: 1, sm: 1.5 } }}
                    >
                      Back
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<KitchenIcon />}
                      onClick={placeOrder}
                      disabled={cart.length === 0}
                      sx={{
                        bgcolor: '#ff6b35',
                        '&:hover': { bgcolor: '#e55a2b' },
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        py: { xs: 1, sm: 1.5 },
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                    >
                      Place Order & KOT
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <CartSummaryPanel />
            </Grid>
          </>
        )}
      </Grid>

      {/* ===== BOTTOM NAVIGATION ===== */}
      <Paper
        sx={{
          position: { xs: 'fixed', sm: 'static' },
          bottom: 0,
          left: 0,
          right: 0,
          p: { xs: 1, sm: 1.5 },
          borderRadius: { xs: '16px 16px 0 0', sm: 3 },
          mt: { xs: 0, sm: 1.5 },
          zIndex: 1200,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          bgcolor: { xs: '#fff', sm: '#fafafa' },
          boxShadow: { xs: '0 -4px 20px rgba(0,0,0,0.08)', sm: '0 2px 12px rgba(0,0,0,0.06)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={cart.reduce((sum, i) => sum + i.quantity, 0)} color="error">
            <CartIcon color="action" />
          </Badge>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
            {cart.length} items / {formatPrice(cartTotal)}
          </Typography>
          {isMobile && cart.length > 0 && (
            <Button size="small" variant="text" onClick={() => setShowMobileCart(true)} sx={{ color: '#ff6b35', fontWeight: 600, fontSize: '0.7rem', ml: 0.5 }}>
              View
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={() => setActiveStep(activeStep - 1)}
              startIcon={<BackIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' }, flex: { xs: 1, sm: 'none' } }}
              size={isMobile ? 'small' : 'medium'}
            >
              Back
            </Button>
          )}
          {activeStep < 2 && (
            <Button
              variant="contained"
              onClick={() => canProceed() && setActiveStep(activeStep + 1)}
              disabled={!canProceed()}
              endIcon={<NextIcon />}
              sx={{
                bgcolor: '#ff6b35',
                '&:hover': { bgcolor: '#e55a2b' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                flex: { xs: 1, sm: 'none' },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
              }}
              size={isMobile ? 'small' : 'medium'}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      {/* ===== MOBILE FAB ===== */}
      {isMobile && cart.length > 0 && activeStep === 0 && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            bgcolor: '#ff6b35',
            '&:hover': { bgcolor: '#e55a2b' },
            boxShadow: '0 4px 20px rgba(255,107,53,0.4)',
          }}
          onClick={() => setShowMobileCart(true)}
        >
          <Badge badgeContent={cart.reduce((sum, i) => sum + i.quantity, 0)} color="error">
            <CartIcon />
          </Badge>
        </Fab>
      )}

      {/* ===== DIALOGS ===== */}
      {/* Modifier Dialog */}
      <Dialog open={modifierDialog} onClose={() => setModifierDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a1a2e', color: '#fff', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          {selectedItemForModifier?.name}
          <Typography variant="caption" sx={{ color: '#ff6b35', display: 'block', mt: 0.3, fontSize: '0.8rem' }}>
            {selectedItemForModifier?.name_urdu}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {selectedItemForModifier && parseVariants(selectedItemForModifier.variants).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e', fontSize: '0.85rem' }}>
                Select Size / سائز منتخب کریں
              </Typography>
              <RadioGroup
                value={selectedVariant?.size || ''}
                onChange={(e) => {
                  const variants = parseVariants(selectedItemForModifier.variants);
                  const v = variants.find((v) => v.size === e.target.value);
                  setSelectedVariant(v);
                }}
              >
                <Grid container spacing={0.5}>
                  {parseVariants(selectedItemForModifier.variants).map((v) => (
                    <Grid item xs={6} key={v.size}>
                      <FormControlLabel
                        value={v.size}
                        control={<Radio sx={{ color: '#ff6b35', '&.Mui-checked': { color: '#ff6b35' }, '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 24 } } }} />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>{v.size}</Typography>
                            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}>
                              {formatPrice(v.price)}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          width: '100%',
                          border: '1px solid',
                          borderColor: selectedVariant?.size === v.size ? '#ff6b35' : '#e0e0e0',
                          borderRadius: 2,
                          p: { xs: 0.5, sm: 1 },
                          m: 0,
                          bgcolor: selectedVariant?.size === v.size ? '#fff8f5' : 'transparent',
                          '& .MuiFormControlLabel-label': { width: '100%' },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </Box>
          )}

          {selectedItemForModifier && selectedItemForModifier.modifiers && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e', fontSize: '0.85rem' }}>
                Add-ons / اضافی
              </Typography>
              <Grid container spacing={0.5}>
                {(() => {
                  let mods = [];
                  try {
                    mods = typeof selectedItemForModifier.modifiers === 'string'
                      ? JSON.parse(selectedItemForModifier.modifiers)
                      : selectedItemForModifier.modifiers;
                  } catch (e) { mods = []; }
                  return mods.map((mod) => (
                    <Grid item xs={6} key={mod.id || mod.name}>
                      <Button
                        fullWidth
                        variant={selectedModifiers.find((m) => m.id === mod.id) ? 'contained' : 'outlined'}
                        onClick={() => toggleModifier(mod)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          py: { xs: 0.8, sm: 1 },
                          bgcolor: selectedModifiers.find((m) => m.id === mod.id) ? '#ff6b35' : 'transparent',
                          borderColor: '#ff6b35',
                          color: selectedModifiers.find((m) => m.id === mod.id) ? '#fff' : '#ff6b35',
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        }}
                      >
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{mod.name}</Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>+{formatPrice(mod.price)}</Typography>
                        </Box>
                      </Button>
                    </Grid>
                  ));
                })()}
              </Grid>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Special Instructions / خصوصی ہدایات"
            value={itemNote}
            onChange={(e) => setItemNote(e.target.value)}
            placeholder="e.g. Less spicy, no onions..."
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid #eee', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setModifierDialog(false)} sx={{ textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={addToCart}
            startIcon={<AddCircleIcon />}
            fullWidth={isMobile}
            sx={{
              bgcolor: '#ff6b35',
              '&:hover': { bgcolor: '#e55a2b' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
            }}
          >
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deal Dialog */}
      <Dialog open={dealDialog} onClose={() => setDealDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ff6b35', color: '#fff', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DealIcon />
            {selectedDeal?.name}
          </Box>
          <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9, display: 'block', mt: 0.3, fontSize: '0.8rem' }}>
            {selectedDeal?.name_urdu}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {selectedDeal?.description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
              {selectedDeal.description}
            </Typography>
          )}

          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff8f5', borderRadius: 2, border: '1px dashed #ffccbc' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e', fontSize: '0.8rem' }}>
              Includes / شامل آئٹمز:
            </Typography>
            {selectedDeal && parseVariants(selectedDeal.deal_items).length > 0 ? (
              parseVariants(selectedDeal.deal_items).map((dItem, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, py: 0.3 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                    • {dItem.name || dItem.item_name || `Item ${idx + 1}`}
                  </Typography>
                  {dItem.quantity && (
                    <Chip size="small" label={`x${dItem.quantity}`} sx={{ height: 18, fontSize: '0.55rem' }} />
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>No items listed</Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Deal Price / ڈیل کی قیمت</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
              {formatPrice(selectedDeal?.price || 0)}
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Special Instructions / خصوصی ہدایات"
            value={itemNote}
            onChange={(e) => setItemNote(e.target.value)}
            placeholder="Any special requests for this deal..."
            sx={{ mt: 1.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid #eee', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setDealDialog(false)} sx={{ textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={addDealToCart}
            startIcon={<AddCircleIcon />}
            fullWidth={isMobile}
            sx={{
              bgcolor: '#ff6b35',
              '&:hover': { bgcolor: '#e55a2b' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
            }}
          >
            Add Deal to Cart
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split Bill Dialog */}
      <Dialog open={splitBillDialog} onClose={() => setSplitBillDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Split Bill / بل تقسیم کریں</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.85rem' }}>
            Divide total of {formatPrice(finalTotal)} into multiple bills
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Number of splits"
            value={splitCount}
            onChange={(e) => setSplitCount(Math.max(2, parseInt(e.target.value) || 2))}
            inputProps={{ min: 2 }}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Each person pays: {formatPrice(finalTotal / splitCount)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setSplitBillDialog(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={confirmSplit} sx={{ bgcolor: '#ff6b35', textTransform: 'none', fontWeight: 600 }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a1a2e', color: '#fff', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon />
            Payment / ادائیگی
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2, bgcolor: '#e8f5e9', borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '0.85rem' }}>Amount Due / واجب الادا رقم</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              PKR {(Number(createdOrder?.total_amount) || finalTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </Typography>
            {createdOrder?.id && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                Order #{createdOrder.id}
              </Typography>
            )}
          </Paper>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e', fontSize: '0.85rem' }}>
            Select Payment Method / ادائیگی کا طریقہ
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {PAYMENT_METHODS.map((method) => (
              <Grid item xs={6} key={method.value}>
                <Button
                  fullWidth
                  variant={paymentMethod === method.value ? 'contained' : 'outlined'}
                  onClick={() => setPaymentMethod(method.value)}
                  startIcon={method.icon}
                  sx={{
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    justifyContent: 'flex-start',
                    bgcolor: paymentMethod === method.value ? '#ff6b35' : 'transparent',
                    borderColor: '#ff6b35',
                    color: paymentMethod === method.value ? '#fff' : '#ff6b35',
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{method.label}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem' }}>{method.labelUrdu}</Typography>
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>

          {paymentMethod === 'cash' && (
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                type="number"
                label="Cash Received / وصول شدہ رقم"
                value={cashReceived}
                onChange={(e) => handleCashReceived(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MoneyIcon color="action" /></InputAdornment>,
                }}
                sx={{ mb: 1.5 }}
              />
              {changeAmount > 0 && (
                <Paper sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 2, borderLeft: '3px solid #ff9800' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100', fontSize: '0.85rem' }}>
                    Change / بقایا: {formatPrice(changeAmount)}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          <Button
            fullWidth
            variant="outlined"
            startIcon={<SplitIcon />}
            onClick={handleSplitBill}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
          >
            Split Bill
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid #eee', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setPaymentDialog(false)} sx={{ textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={processPayment}
            disabled={isProcessingPayment || (paymentMethod === 'cash' && !cashReceived)}
            startIcon={isProcessingPayment ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            fullWidth={isMobile}
            sx={{
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 4,
            }}
          >
            {isProcessingPayment ? 'Processing...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptDialog} onClose={() => setReceiptDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a1a2e', color: '#fff', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            Receipt / رسید
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#f5f5f5' }}>
          <ThermalReceipt
            order={createdOrder}
            settings={settings}
            paymentMethod={paymentMethod}
            cashReceived={cashReceived}
            changeAmount={changeAmount}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid #eee', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button
            fullWidth={isMobile}
            variant="outlined"
            onClick={() => {
              setReceiptDialog(false);
              setCreatedOrder(null);
              setActiveStep(0);
              setCart([]);
              setCustomerName('');
              setCustomerPhone('');
              setDeliveryAddress('');
              setRiderName('');
              setDiscountPercent(0);
              setDiscountAmount(0);
              setServiceCharge(0);
              setOrderNote('');
              setSelectedTable(null);
              showMessage('🔄 New order started!', 'info');
            }}
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            New Order
          </Button>
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={printReceipt}
            startIcon={<PrintIcon />}
            sx={{
              bgcolor: '#ff6b35',
              '&:hover': { bgcolor: '#e55a2b' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
            }}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Cart Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={showMobileCart}
        onClose={() => setShowMobileCart(false)}
        onOpen={() => setShowMobileCart(true)}
        sx={{ '& .MuiDrawer-paper': { borderRadius: '16px 16px 0 0', maxHeight: '85vh' } }}
      >
        <Box sx={{ p: 2, bgcolor: '#1a1a2e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '16px 16px 0 0' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Cart / کارٹ ({cart.reduce((sum, i) => sum + i.quantity, 0)} items)
          </Typography>
          <Box>
            {cart.length > 0 && (
              <Button color="inherit" onClick={() => setCart([])} startIcon={<DeleteIcon />} size="small">
                Clear
              </Button>
            )}
            <IconButton color="inherit" onClick={() => setShowMobileCart(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
          {cart.length > 0 ? (
            cart.map((item) => (
              <Card key={item.id} sx={{ mb: 1.5, borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.name}
                        {item.isDeal && <Chip size="small" label="DEAL" color="secondary" sx={{ ml: 0.5, height: 18, fontSize: '0.55rem' }} />}
                        {item.variant_size && <Chip size="small" label={item.variant_size} color="primary" sx={{ ml: 0.5, height: 18, fontSize: '0.55rem' }} />}
                      </Typography>
                      {item.modifiers.length > 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6rem' }}>
                          + {item.modifiers.map((m) => m.name).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small" onClick={() => updateQuantity(item.id, -1)} sx={{ bgcolor: '#f0f0f0', width: 28, height: 28 }}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
                        {item.quantity}
                      </Typography>
                      <IconButton size="small" onClick={() => updateQuantity(item.id, 1)} sx={{ bgcolor: '#f0f0f0', width: 28, height: 28 }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {formatPrice((item.unit_price + item.modifiers_price) * item.quantity)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
              <CartIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography>Cart is empty</Typography>
            </Box>
          )}
        </Box>
        {cart.length > 0 && (
          <Box sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="h6" fontWeight={700}>Total</Typography>
              <Typography variant="h6" fontWeight={700} color="#2e7d32">{formatPrice(finalTotal)}</Typography>
            </Box>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                setShowMobileCart(false);
                if (activeStep < 2) setActiveStep(2);
              }}
              sx={{ bgcolor: '#ff6b35', fontWeight: 700, py: 1.5, borderRadius: 2 }}
            >
              Proceed to Checkout
            </Button>
          </Box>
        )}
      </SwipeableDrawer>
    </Box>
  );
};

export default Billing;